/**
 * Feature Flag Service
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/api';
import type {
  FeatureFlag,
  TenantFeatureOverride,
  TenantFeatureFlag,
} from '../types';

export interface CreateFeatureFlagDto {
  key: string;
  description?: string;
  defaultValue: boolean;
  isCritical?: boolean;
}

export interface UpdateFeatureFlagDto {
  description?: string;
  defaultValue?: boolean;
  isCritical?: boolean;
}

export interface BulkSetFlagOverridesResult {
  applied: number;
  errors: Array<{ tenantId: string; error: string }>;
}

export const featureFlagService = {
  /**
   * Get all feature flags
   */
  async getAll(): Promise<FeatureFlag[]> {
    const response = await apiClient.get<FeatureFlag[]>(API_ENDPOINTS.FEATURE_FLAGS);
    return response.data;
  },

  /**
   * Get a single feature flag
   */
  async getByKey(key: string): Promise<FeatureFlag> {
    const response = await apiClient.get<FeatureFlag>(
      `${API_ENDPOINTS.FEATURE_FLAGS}/${key}`
    );
    return response.data;
  },

  /**
   * Create a new feature flag
   */
  async create(dto: CreateFeatureFlagDto): Promise<FeatureFlag> {
    const response = await apiClient.post<FeatureFlag>(
      API_ENDPOINTS.FEATURE_FLAGS,
      dto
    );
    return response.data;
  },

  /**
   * Update a feature flag
   */
  async update(key: string, dto: UpdateFeatureFlagDto): Promise<FeatureFlag> {
    const response = await apiClient.patch<FeatureFlag>(
      `${API_ENDPOINTS.FEATURE_FLAGS}/${key}`,
      dto
    );
    return response.data;
  },

  /**
   * Delete a feature flag
   */
  async delete(key: string): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.FEATURE_FLAGS}/${key}`);
  },

  /**
   * Get all tenant overrides for a flag
   */
  async getOverrides(key: string): Promise<TenantFeatureOverride[]> {
    const response = await apiClient.get<TenantFeatureOverride[]>(
      `${API_ENDPOINTS.FEATURE_FLAGS}/${key}/overrides`
    );
    return response.data;
  },

  /**
   * Set a tenant override for a flag
   */
  async setTenantOverride(
    key: string,
    tenantId: string,
    value: boolean
  ): Promise<TenantFeatureOverride> {
    const response = await apiClient.post<TenantFeatureOverride>(
      `${API_ENDPOINTS.FEATURE_FLAGS}/${key}/overrides/${tenantId}`,
      { value }
    );
    return response.data;
  },

  /**
   * Remove a tenant override
   */
  async removeTenantOverride(key: string, tenantId: string): Promise<void> {
    await apiClient.delete(
      `${API_ENDPOINTS.FEATURE_FLAGS}/${key}/overrides/${tenantId}`
    );
  },

  /**
   * Get all flags for a tenant with effective values
   */
  async getTenantFlags(tenantId: string): Promise<TenantFeatureFlag[]> {
    const response = await apiClient.get<TenantFeatureFlag[]>(
      `${API_ENDPOINTS.FEATURE_FLAGS}/tenant/${tenantId}`
    );
    return response.data;
  },

  /**
   * Bulk set overrides for a tenant
   */
  async bulkSetTenantOverrides(
    tenantId: string,
    overrides: Array<{ key: string; value: boolean }>
  ): Promise<void> {
    await apiClient.post(
      `${API_ENDPOINTS.FEATURE_FLAGS}/tenant/${tenantId}/bulk`,
      { overrides }
    );
  },

  /**
   * Set the same override value across multiple tenants for one flag
   */
  async bulkSetFlagOverrides(
    key: string,
    tenantIds: string[],
    value: boolean
  ): Promise<BulkSetFlagOverridesResult> {
    const response = await apiClient.post<BulkSetFlagOverridesResult>(
      `${API_ENDPOINTS.FEATURE_FLAGS}/${key}/bulk`,
      { tenantIds, value }
    );
    return response.data;
  },
};
